import { useEffect, useState, useRef } from 'react';
import { database } from '../config/firebase';
import { ref, onValue, push, query, limitToLast, serverTimestamp, orderByChild, remove } from 'firebase/database';

export interface Message {
    id: string;
    userId: string;
    userName: string;
    userAvatar?: string;
    message: string;
    timestamp: number;
    type: 'text' | 'system';
    attachmentUrl?: string;
    attachmentType?: 'image' | 'video' | 'file';
}

export const useChatMessages = (initialLimit: number = 50) => {
    const [limit, setLimit] = useState(initialLimit);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const messagesRef = ref(database, 'chat/messages');
        const q = query(messagesRef, orderByChild('timestamp'), limitToLast(limit));

        const unsubscribe = onValue(q, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const messageList: Message[] = Object.entries(data).map(([key, value]: [string, any]) => ({
                    id: key,
                    ...value,
                }));
                // Sort by timestamp just in case
                messageList.sort((a, b) => a.timestamp - b.timestamp);
                setMessages(messageList);
            } else {
                setMessages([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [limit]);

    const sendMessage = async (userId: string, userName: string, text: string, attachmentUrl?: string, attachmentType?: 'image' | 'video' | 'file', userAvatar?: string) => {
        if (!text.trim() && !attachmentUrl) return;

        const messagesRef = ref(database, 'chat/messages');
        await push(messagesRef, {
            userId,
            userName,
            userAvatar: userAvatar || null,
            message: text,
            timestamp: serverTimestamp(),
            type: 'text',
            attachmentUrl: attachmentUrl || null,
            attachmentType: attachmentType || null
        });
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const deleteMessage = async (messageId: string) => {
        const messageRef = ref(database, `chat/messages/${messageId}`);
        await remove(messageRef);
    };

    const loadMore = () => {
        setLimit(prev => prev + 20);
    };

    return { messages, loading, sendMessage, deleteMessage, scrollToBottom, messagesEndRef, loadMore };
};
